/* ======= ProductInfo class
 * Listens for cart updates and product variant changes :: Handles quantity rule boundary adjustments ======= */
if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      constructor() {
        super();
        // Record recently viewed products
        if (!!window.localStorage) this.addRecentlyViewedProduct();

        this.quantityForm = this.querySelector(".quantity-selector"); // Quantity selector container
        this.input = this.querySelector(".quantity-input"); // Product quantity input
        if (!this.quantityForm || !this.input) return;

        this.currentVariant = this.querySelector("input.product-variant-id");
        this.submitButton = this.querySelector('button[type="submit"]'); // Add to cart button
        // Initialize quantity boundary adjustments
        this.setQuantityBoundries();

        // Subscribe to cart updates :: Quick product preview does not require subscription
        if (!this.dataset.originalSection) {
          // Subscribe to cart updates -> retrieve the latest quantity rules and apply adjustments
          this.cartUpdateUnsubscriber = webvista.subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }

        // Subscribe to product variant changes
        this.variantChangeUnsubscriber = webvista.subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (event.data.sectionId !== this.dataset.section) return;
            // When the variant changes, update the quantity rules
            this.updateQuantityRules(event.data.html);
            // Update the input field boundaries
            this.setQuantityBoundries();
          },
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      /* ====== Re-fetch the latest quantity rules
       * Adjust the quantity selector boundary rules ====== */
      fetchQuantityRules() {
        if (!this.currentVariant || !this.currentVariant.value) return;

        webvista.fetchHtml(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.sectionId}`).then(html=>{
          this.updateQuantityRules(html);
          this.setQuantityBoundries();
        }).catch((e) => {
          webvista.popToast(window["accessibilityStrings"]["unknownError"], "error");
        });
      }

     /* ====== Adjust quantity selector boundary values
      * Updates the min and max values of the quantity input field
      * Triggered when the cart is updated or when the product variant selector changes ======= */
      setQuantityBoundries() {
        const data = {
          cartQuantity: this.input.dataset.cartQuantity
            ? parseInt(this.input.dataset.cartQuantity)
            : 0, // Quantity already added to the cart
          min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1, // Minimum quantity that can be added to the cart at one time
          max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null, // Maximum quantity allowed for a specific product SKU in the cart during a single checkout
          step: this.input.step ? parseInt(this.input.step) : 1,
        };

        let min = data.min;
        // Maximum addable quantity = maximum purchasable quantity - quantity already in the cart
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        // Minimum input value must not exceed the step value
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.input.min = min;
        this.input.max = max;
        this.input.value = min;
        webvista.publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      /* ====== Update quantity selector rules
       * .quantity-input: Updates data-cart-quantity, data-min, data-max, and step attributes
       * .quantity-label: Displays the quantity of the current variant already added to the cart ====== */
      updateQuantityRules(html) {
        const quantityFormSource = html.querySelector(".quantity-selector");
        const selectors = [".quantity-input", ".quantity-label"];

        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormSource.querySelector(selector);

          if (!current || !updated) continue;

          if (selector === ".quantity-input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null)
                current.setAttribute(attribute, valueUpdated);
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      /* ====== Add recently viewed product ====== */
      addRecentlyViewedProduct() {
        if (!this.dataset.id) return;
        const recentlyViewedData = webvista.retrieveData(RECENTLY_VIEWED_KEY) || [];
        const index = recentlyViewedData.indexOf(this.dataset.id);
        if (index !== -1) {
          recentlyViewedData.splice(index, 1); // Remove the existing record
        }
        recentlyViewedData.push(this.dataset.id); // Add a new record
        // Remove older entries to keep a maximum of 10 items
        if (recentlyViewedData.length > 10) {
          recentlyViewedData.splice(0, recentlyViewedData.length - 10);
        }
        webvista.storeData(RECENTLY_VIEWED_KEY, recentlyViewedData);
      }
      get sectionId() {
        return this.dataset.section;
      }
    },
  );
}